import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * PERF-UX (plan v13, 21.10.2026): cache odczytów z pamięcią — ekran pokazuje się OD RAZU z ostatnich danych,
 * a odświeżenie idzie w tle (stale-while-revalidate, jak TanStack Query + persister, ale bez nowej zależności).
 *
 * - `useCached(key, fetcher, { staleMs, persist })` → { data, loading, refreshing, error, refresh }
 *   `loading` = true TYLKO gdy nie ma nic w cache (pierwsze wejście w życiu); potem zawsze są dane, a `refreshing` mówi, że trwa odświeżanie.
 * - Pamięć: Map w module (między ekranami bez przeładowania) + localStorage (po przeładowaniu strony; klucz z id użytkownika,
 *   wpisy starsze niż MAX_AGE są pomijane). Wartość musi być serializowalna (JSON).
 * - Deduplikacja: dwa komponenty z tym samym kluczem = jedno żądanie.
 * - `invalidate(prefix)` po zapisie — następne użycie odświeży; `setCached(key, data)` = zapis optymistyczny.
 * - Przy powrocie do zakładki (visibilitychange) wpisy przeterminowane odświeżają się same.
 *
 * Docs: docs/kontekst/2026-10-21_PERF-UX_RESEARCH.md (sekcja 4, A1).
 */
const MEM = new Map();          // key → { data, at }
const INFLIGHT = new Map();     // key → Promise
const LISTENERS = new Map();    // key → Set<fn>
const PREFIX = 'mams.cache.v1:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
let scope = 'anon';

/** Zakres cache = użytkownik (po zalogowaniu). Zmiana użytkownika czyści pamięć. */
export function setCacheScope(userId) {
  const next = userId || 'anon';
  if (next === scope) return;
  scope = next;
  MEM.clear();
  INFLIGHT.clear();
}

const lsKey = (key) => `${PREFIX}${scope}:${key}`;

function readLs(key) {
  try {
    const raw = localStorage.getItem(lsKey(key));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.at !== 'number' || Date.now() - v.at > MAX_AGE_MS) return null;
    return v;
  } catch { return null; }
}
function writeLs(key, entry) {
  try { localStorage.setItem(lsKey(key), JSON.stringify(entry)); } catch { /* brak miejsca / prywatny tryb — cache tylko w pamięci */ }
}

export function getCached(key) {
  const m = MEM.get(key);
  if (m) return m;
  const v = readLs(key);
  if (v) MEM.set(key, v);
  return v;
}

function notify(key) {
  const set = LISTENERS.get(key);
  if (set) set.forEach(fn => { try { fn(); } catch { /* noop */ } });
}

/** Zapis optymistyczny / ręczne ustawienie danych pod kluczem (np. po zapisie formularza). */
export function setCached(key, data, { persist = true } = {}) {
  const entry = { data, at: Date.now() };
  MEM.set(key, entry);
  if (persist) writeLs(key, entry);
  notify(key);
}

/** Unieważnia wpisy o kluczu zaczynającym się od `prefix` (następne użycie odświeży w tle, dane zostają widoczne). */
export function invalidate(prefix) {
  for (const [k, v] of MEM) if (k.startsWith(prefix)) MEM.set(k, { ...v, at: 0 });
  try {
    const p = `${PREFIX}${scope}:${prefix}`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(p)) { const v = JSON.parse(localStorage.getItem(k) || 'null'); if (v) localStorage.setItem(k, JSON.stringify({ ...v, at: 0 })); }
    }
  } catch { /* noop */ }
  for (const k of LISTENERS.keys()) if (k.startsWith(prefix)) notify(k);
}

/** Pobranie z deduplikacją; `force` pomija świeżość. Zwraca dane (i zapisuje do cache). */
export async function fetchCached(key, fetcher, { staleMs = 30_000, persist = true, force = false } = {}) {
  const cur = getCached(key);
  if (!force && cur && Date.now() - cur.at < staleMs) return cur.data;
  if (INFLIGHT.has(key)) return INFLIGHT.get(key);
  const p = (async () => {
    try {
      const data = await fetcher();
      setCached(key, data, { persist });
      return data;
    } finally { INFLIGHT.delete(key); }
  })();
  INFLIGHT.set(key, p);
  return p;
}

/** Rozgrzanie cache (np. po zalogowaniu) — bez komponentu. Błędy ignorowane. */
export function prefetch(key, fetcher, opts) {
  return fetchCached(key, fetcher, opts).catch(() => null);
}

export function useCached(key, fetcher, { staleMs = 30_000, persist = true, enabled = true } = {}) {
  const [, bump] = useState(0);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const fetcherRef = useRef(fetcher); fetcherRef.current = fetcher;
  const entry = enabled && key ? getCached(key) : null;

  const refresh = useCallback(async (force = true) => {
    if (!enabled || !key) return;
    setRefreshing(true);
    try { await fetchCached(key, () => fetcherRef.current(), { staleMs, persist, force }); setError(null); }
    catch (e) { setError(e); }
    finally { setRefreshing(false); bump(n => n + 1); }
  }, [key, staleMs, persist, enabled]);

  // nasłuch zmian pod kluczem (setCached / invalidate z innego miejsca)
  useEffect(() => {
    if (!key) return;
    const fn = () => bump(n => n + 1);
    if (!LISTENERS.has(key)) LISTENERS.set(key, new Set());
    LISTENERS.get(key).add(fn);
    return () => { LISTENERS.get(key)?.delete(fn); };
  }, [key]);

  // pierwsze użycie / przeterminowanie → odświeżenie w tle (dane z cache zostają widoczne)
  useEffect(() => {
    if (!enabled || !key) return;
    const cur = getCached(key);
    if (!cur || Date.now() - cur.at >= staleMs) refresh(false);
  }, [key, enabled, staleMs, refresh]);

  // powrót do zakładki → odświeżenie, jeśli przeterminowane
  useEffect(() => {
    if (!enabled || !key) return;
    const onVis = () => { if (document.visibilityState === 'visible') { const cur = getCached(key); if (!cur || Date.now() - cur.at >= staleMs) refresh(false); } };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [key, enabled, staleMs, refresh]);

  return { data: entry ? entry.data : undefined, loading: enabled && !entry, refreshing, error, refresh, at: entry?.at ?? 0 };
}

/**
 * Wzorzec dla istniejących stron bez przepisywania na hook: najpierw `apply(dane z cache)` (od razu, jeśli są),
 * potem świeże dane z sieci i znowu `apply`. Zwraca świeże dane (albo rzuca błąd fetchera).
 * `staleMs` > 0 — gdy wpis w cache jest młodszy, sieć jest pomijana (dane rzadko zmienne: konfiguracja, kategorie integratora).
 */
export async function loadWithCache(key, fetcher, apply, { persist = true, staleMs = 0 } = {}) {
  const cur = getCached(key);
  if (cur) { try { apply(cur.data, true); } catch { /* noop */ } }
  if (cur && staleMs > 0 && Date.now() - cur.at < staleMs) return cur.data;
  const data = await fetchCached(key, fetcher, { staleMs: 0, persist, force: true });
  apply(data, false);
  return data;
}
